import React from 'react';
import styled from 'styled-components';
import FA from '../../atoms/FontAwesome';
import Grid from '../../atoms/Grid';
import Label from '../../atoms/Label';
import LocalizedMask from '../../atoms/LocalizedMask';
import RangeInput from '../../atoms/RangeInput';
import Select from '../../atoms/Select';
import TextBox from '../../atoms/TextBox';
import type { CampaignForm } from './EditCampaign';

const AMOUNT_UNIT_TYPES = [
  { label: '還元率(％)', value: 'percent' },
  { label: '還元金額', value: 'absolute' },
] as const;

type Props = {
  selectedPointRulesType: CampaignForm['selectedPointRulesType'];
  uniformAmountRule: AmountRule;
  customAmountRules: AmountRule[];
  onUniformAmountRuleChange?: (rule: AmountRule) => void;
  onCustomRulesChange?: (rules: AmountRule[]) => void;
  disabled?: boolean;
};

export type AmountRule = {
  amount: string | null;
  unit: typeof AMOUNT_UNIT_TYPES[number]['value'];
  from: number | null;
  to: number | null;
};

const CustomAmountRuleWrapper = styled(Grid.Item)`
  padding: 8px;
  border: solid 1px #ececec;
  margin: 8px 0;
  position: relative;
`;

const AddButton = styled(FA)<{ disabled?: boolean }>`
  position: absolute;
  top: 8px;
  right: 12px;
  cursor: pointer;
  color: #999;
  font-size: 20px;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 9999px;
  background: #ececec;
  ${(props) => (props.disabled ? 'pointer-events: none;' : '')}
`;

const TrashButton = styled(FA)<{ disabled?: boolean }>`
  position: absolute;
  top: 8px;
  right: 48px;
  cursor: pointer;
  color: #999;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  ${(props) => (props.disabled ? 'pointer-events: none;' : '')}
`;

const AmountBasedPointRules = ({
  selectedPointRulesType,
  uniformAmountRule,
  customAmountRules,
  onUniformAmountRuleChange,
  onCustomRulesChange,
  disabled = false,
}: Props) => {
  return (
    <>
      {['allAmountUniform', 'moneyAmountUniform'].includes(selectedPointRulesType) && (
        <UniformAmountRule
          rule={uniformAmountRule}
          onChange={(rule) => onUniformAmountRuleChange?.(rule)}
          disabled={disabled}
        />
      )}

      {['allAmountCustom', 'moneyAmountCustom'].includes(selectedPointRulesType) && (
        <CustomAmountRule
          rules={customAmountRules}
          onChange={(rules) => onCustomRulesChange?.(rules)}
          disabled={disabled}
        />
      )}
    </>
  );
};

const UniformAmountRule = ({
  rule,
  onChange,
  disabled,
}: {
  rule: AmountRule;
  onChange: (rule: AmountRule) => void;
  disabled: boolean;
}) => {
  return (
    <>
      <Grid.Item size={12} flexDirection="column">
        <Label>還元設定</Label>
      </Grid.Item>
      <Grid.Item container nextRow columnGap={8} alignCenter>
        <Grid.Item nextRow size={3}>
          <Select
            isMulti={false}
            fullWidth
            options={AMOUNT_UNIT_TYPES}
            value={AMOUNT_UNIT_TYPES.find((s) => s.value === rule.unit)}
            onChange={(option) => option && onChange({ ...rule, unit: option.value })}
            placeholder="付与する量の単位を選択してください"
            isDisabled={disabled}
          />
        </Grid.Item>
        <Grid.Item size={3}>
          {rule.unit === 'percent' ? (
            <TextBox
              fullWidth
              pattern="\d+(?:\.\d+)?"
              syncValue={rule.amount ?? undefined}
              onChange={(v) => onChange({ ...rule, amount: v ? v.target.value : null })}
              disabled={disabled}
            />
          ) : (
            <LocalizedMask
              value={rule.amount ?? undefined}
              onChange={(v) => onChange({ ...rule, amount: v ? v.target.value : null })}
            >
              {({ value, ...inputProps }) => (
                <TextBox fullWidth syncValue={value} {...inputProps} disabled={disabled} />
              )}
            </LocalizedMask>
          )}
        </Grid.Item>
      </Grid.Item>
    </>
  );
};

const CustomAmountRule = ({
  rules,
  onChange,
  disabled,
}: {
  rules: AmountRule[];
  onChange: (rules: AmountRule[]) => void;
  disabled: boolean;
}) => {
  return (
    <>
      {rules.map((rule, index) => (
        <CustomAmountRuleWrapper key={index} size={12} container rowGap={8}>
          <Grid.Item size={9} alignCenter>
            取引金額
          </Grid.Item>
          <Grid.Item size={9} nextRow>
            <RangeInput>
              <LocalizedMask
                value={rule.from?.toString()}
                onChange={(v) =>
                  onChange(
                    rules.map((r, i) =>
                      i === index ? { ...r, from: v ? Number(v.target.value) : null } : r
                    )
                  )
                }
              >
                {(inputProps) => (
                  <RangeInput.Field
                    {...inputProps}
                    placeholder="対象金額の下限値"
                    disabled={disabled}
                  />
                )}
              </LocalizedMask>

              <RangeInput.Icon />

              <LocalizedMask
                value={rule.to?.toString()}
                onChange={(v) =>
                  onChange(
                    rules.map((r, i) =>
                      i === index ? { ...r, to: v ? Number(v.target.value) : null } : r
                    )
                  )
                }
              >
                {(inputProps) => (
                  <RangeInput.Field
                    {...inputProps}
                    placeholder="対象金額の上限値"
                    disabled={disabled}
                  />
                )}
              </LocalizedMask>
            </RangeInput>
          </Grid.Item>

          <Grid.Item size={9} alignCenter nextRow>
            <Label>還元設定</Label>
          </Grid.Item>
          <Grid.Item size={9} container nextRow alignCenter columnGap={8}>
            <Grid.Item size={5}>
              <Select
                isMulti={false}
                fullWidth
                options={AMOUNT_UNIT_TYPES}
                value={AMOUNT_UNIT_TYPES.find((s) => s.value === rule.unit)}
                onChange={(option) =>
                  option &&
                  onChange(rules.map((r, i) => (i === index ? { ...r, unit: option.value } : r)))
                }
                placeholder="付与する量の単位を選択してください"
                isDisabled={disabled}
              />
            </Grid.Item>
            <Grid.Item size={5}>
              {rule.unit === 'percent' ? (
                <TextBox
                  fullWidth
                  pattern="\d+(?:\.\d+)?"
                  syncValue={rule.amount ?? undefined}
                  onChange={(v) =>
                    onChange(
                      rules.map((r, i) => (i === index ? { ...r, amount: v.target.value } : r))
                    )
                  }
                  disabled={disabled}
                />
              ) : (
                <LocalizedMask
                  value={rule.amount ?? undefined}
                  onChange={(v) =>
                    onChange(
                      rules.map((r, i) =>
                        i === index ? { ...r, amount: v ? v.target.value : null } : r
                      )
                    )
                  }
                >
                  {({ value, ...inputProps }) => (
                    <TextBox fullWidth syncValue={value} {...inputProps} disabled={disabled} />
                  )}
                </LocalizedMask>
              )}
            </Grid.Item>
          </Grid.Item>
          <AddButton
            icon="plus"
            onClick={() =>
              onChange([...rules, { amount: null, unit: 'percent', from: null, to: null }])
            }
            disabled={disabled}
          />
          <TrashButton
            icon="trash-alt"
            onClick={() => onChange(rules.filter((_, i) => i !== index))}
            disabled={disabled || rules.length <= 1}
          />
        </CustomAmountRuleWrapper>
      ))}
    </>
  );
};

export default AmountBasedPointRules;
